import { useCallback, useSyncExternalStore } from 'react';

/**
 * Ansichtseinstellungen: was man sieht, nicht was im Archiv steht.
 *
 * Gemeinsam ist ihnen dreierlei, und daraus ergibt sich die Bauform:
 *
 *  - **Sie überdauern den Bildwechsel.** Wer die Rahmen ausblendet, um Fotos
 *    anzusehen, will sie nicht bei jedem Bild neu wegklicken – genau diese
 *    Arbeit soll die Einstellung ja abnehmen. Deshalb `localStorage`.
 *  - **Sie gelten fensterweit.** Bildseite und Vollbild sind gleichzeitig
 *    eingehängt: das Vollbild öffnet sich über der Seite, die darunter bestehen
 *    bleibt. Mit `useState` je Komponente erreichte ein Umschalten im Vollbild
 *    die Seite dahinter nicht, und beim Schließen stünde man vor dem alten
 *    Stand. `useSyncExternalStore` hält beide zusammen.
 *  - **Sie sind harmlos.** Geht das Speichern schief – privater Modus,
 *    gesperrte Site-Daten –, gilt die Einstellung eben nur für diese Sitzung.
 *    Ein Fehler ist das nicht und wird auch nicht als solcher gemeldet.
 */
function createToggle(key: string, fallback: boolean) {
  const read = (): boolean => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? fallback : stored === '1';
    } catch {
      return fallback;
    }
  };

  let value = read();
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const set = (next: boolean): void => {
    if (next === value) return;
    value = next;
    try {
      localStorage.setItem(key, next ? '1' : '0');
    } catch {
      /* Nicht speichern zu können ist kein Grund, nicht umzuschalten. */
    }
    for (const listener of listeners) listener();
  };

  /**
   * `[wert, setzen]` – wie `useState`, nur fensterweit.
   *
   * Der dritte Parameter von `useSyncExternalStore` ist gesetzt, obwohl diese
   * Anwendung nicht auf dem Server gerendert wird: ohne ihn wirft der erste
   * Durchlauf in einer Umgebung ohne `localStorage`.
   */
  return function useToggle(): [boolean, (next: boolean) => void] {
    const current = useSyncExternalStore(
      subscribe,
      () => value,
      () => fallback,
    );
    return [current, useCallback((next: boolean) => set(next), [])];
  };
}

/**
 * Ob die Markierungen über den Bildern liegen.
 *
 * Standard ist „ja": ein Bild ohne Rahmen sieht aus wie eines ohne
 * Markierungen, und wer die Anwendung zum ersten Mal öffnet, soll die Arbeit
 * sehen, die darin steckt.
 */
export const useRegionVisibility = createToggle('fb:show-regions', true);

/**
 * Ob die Bildliste neben dem Bild steht.
 *
 * Standard ist „ja", aber die Spalte erscheint ohnehin erst ab einer Breite,
 * auf der sie dem Abzug nichts wegnimmt – und wer sie dort trotzdem nicht
 * mag, klappt sie einmal weg.
 */
export const useStripVisibility = createToggle('fb:show-media-strip', true);
